import axios from 'axios';
import dayjs from 'dayjs';
import { Dispatch, useEffect, useState } from 'react';
import { Setting } from '../App';
import { CUSTOMER_API_URL } from '../constants';
import { PurchasedProductsApiType } from '../types/api/purchased-products-api-type';
import { RecommendProductVariantsApiType } from '../types/api/recommend-product-variants-api-type';
import { SubscriptionsApiType, SubscriptionType, SubscriptionStatus } from '../types/api/subscriptions-api-type';
import { createProductInfo, ProductInfoViewType } from '../types/view/product-info-view-type';
import { RecommendProductInfoViewType } from '../types/view/recommend-product-info-view-type';

export const useSubscriptionDetail = (
  setting: Setting,
  setSubscriptionContractId: Dispatch<string>,
  setRegisteredProducts: Dispatch<ProductInfoViewType[]>,
  setPausedProducts: Dispatch<ProductInfoViewType[]>,
  setRecommendProducts: Dispatch<RecommendProductInfoViewType[]>,
  setLoadCompleted: Dispatch<boolean>,
  setSubscriptionStatus: Dispatch<SubscriptionStatus>,
) => {
  useEffect(() => {
    getSubscriptionDetail(
      () => {
        setLoadCompleted(true);
      },
      (e) => {
        console.log(e);
      }
    );
  }, [
    setPausedProducts,
    setRecommendProducts,
    setRegisteredProducts,
    setSubscriptionContractId,
    setSubscriptionStatus,
    setting.token,
  ]);

  const [subscription, setSubscription] = useState<SubscriptionType>();

  const getSubscriptionDetail = (success?: () => void, failed?: (e: Error) => void) => {
    (async () => {
      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getSubscriptions`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const subscriptions = (response.data as SubscriptionsApiType).filter(
          (subscription) => subscription.status === 'ACTIVE' || subscription.status === 'PAUSED'
        );

        if (subscriptions.length === 0) return;

        const subscription = subscriptions[0];
        setSubscription(subscription);
        setSubscriptionContractId(subscription.subscriptionContractId);
        setSubscriptionStatus(subscription.status);

        const products = subscription.subscriptionLines.map((subscriptionLine) => {
          return createProductInfo(subscriptionLine, true);
        });
        setRegisteredProducts(products);

        const purchasedProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getPurchasedProducts`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const pausedProducts = (purchasedProductsResponse.data as PurchasedProductsApiType).reduce(
          (result, purchasedProduct) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== purchasedProduct.variantId)) {
              result.push({
                productTitle: purchasedProduct.productTitle,
                image: purchasedProduct.image,
                variantTitle: purchasedProduct.variantTitle,
                originalPrice: purchasedProduct.originalPrice,
                price: purchasedProduct.price,
                quantity: 0,
                variantId: purchasedProduct.variantId,
                planId: purchasedProduct.planId,
                url: purchasedProduct.url,
                active: false,
              });
            }

            return result;
          },
          [] as ProductInfoViewType[]
        );

        setPausedProducts(pausedProducts);

        const recommendProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getRecommendProductVariants`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const recommendProducts = (recommendProductsResponse.data as RecommendProductVariantsApiType).reduce(
          (result, recommendProduct) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== recommendProduct.variantId)) {
              result.push({
                productTitle: recommendProduct.productTitle,
                image: recommendProduct.image,
                variantTitle: recommendProduct.variantTitle,
                originalPrice: recommendProduct.originalPrice,
                price: recommendProduct.price,
                quantity: 0,
                variantId: recommendProduct.variantId,
                planId: recommendProduct.planId,
                message: recommendProduct.message,
                url: recommendProduct.url,
                active: false,
              });
            }

            return result;
          },
          [] as RecommendProductInfoViewType[]
        );

        setRecommendProducts(recommendProducts);

        if (success) {
          success();
        }
        setLoadCompleted(true);
      } catch (e) {
        if (failed) {
          failed(e);
        }
      }
    })();
  };

  const getRegisteredProducts = (
    success: (infos: ProductInfoViewType[]) => void,
    failed?: (message: string) => void
  ) => {
    (async () => {
      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getSubscriptions`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const subscriptions = (response.data as SubscriptionsApiType).filter(
          (subscription) => subscription.status === 'ACTIVE' || subscription.status === 'PAUSED'
        );
        if (subscriptions.length === 0) return;

        const subscription = subscriptions[0];
        setSubscriptionContractId(subscription.subscriptionContractId);
        setSubscriptionStatus(subscription.status);

        const products = subscription.subscriptionLines.map((subscriptionLine) => {
          return createProductInfo(subscriptionLine, true);
        });

        success(products);
      } catch (e) {
        if (failed) {
          failed(e.message);
        }
      }
    })();
  };

  return {
    subscription,
    setSubscription,
    getSubscriptionDetail,
    getRegisteredProducts,
  };
};
